import { 
  Store, 
  Layers, 
  ChevronRight, 
  ExternalLink, 
  CheckCircle2, 
  Clock, 
  Users, 
  Package, 
  Building2,
  ArrowUpRight
} from 'lucide-react';
import { Button } from '../../../components/ui/Button.jsx';
import { Badge } from '../../../components/ui/Badge.jsx';
import { cn } from '../../../lib/utils.js';
import { 
  TOTAL_SKU_COUNT, 
  TENANTS_COUNT, 
  ACTIVE_TENANTS, 
  PRICE_CATALOG_SYNC 
} from '../executive-data.js';

export const SHOP_CANONICAL_CATEGORIES = [
  { name: 'Brandmeldetechnik (BMA)', subcategories: 8, skus: 384 },
  { name: 'Einbruchmeldetechnik (EMA)', subcategories: 9, skus: 426 },
  { name: 'Videoüberwachung (VSS)', subcategories: 7, skus: 312 },
  { name: 'Zutrittskontrolle & Fluchtweg', subcategories: 5, skus: 178 },
  { name: 'Signalgeber & Akkumulatoren', subcategories: 5, skus: 104 },
  { name: 'Netzwerk & Übertragungstechnik', subcategories: 4, skus: 68 },
];

export function ShopPillarCard({
  skuCount = null,
  tenants = null,
  catalogTimestamp = null,
  isLive = false,
  onNavigate,
  onOpenTab,
  isDetailed = false,
  className,
}) {
  const handleStorefrontClick = () => {
    if (onNavigate) {
      onNavigate('/');
    }
  };

  const handleAdminClick = () => {
    if (onNavigate) {
      onNavigate('/admin');
    }
  };

  const handleOpenPillar = () => {
    if (onOpenTab) {
      onOpenTab('shop');
    } else if (onNavigate) {
      onNavigate('/hub/shop');
    }
  };

  const displaySkuCount = skuCount !== null && skuCount !== undefined ? skuCount : TOTAL_SKU_COUNT;
  const displayTenants = tenants !== null && tenants !== undefined ? tenants : ACTIVE_TENANTS;
  const displayTimestamp = catalogTimestamp || PRICE_CATALOG_SYNC.formattedDate;

  return (
    <div
      data-testid="pillar-card-shop"
      className={cn(
        'flex flex-col justify-between rounded-2xl border border-slate-200 bg-white p-6 shadow-xs transition-all hover:border-[var(--primary,#e3000b)] hover:shadow-sm',
        className
      )}
    >
      <div className="space-y-4">
        {/* Header Badge Strip */}
        <div className="flex items-center justify-between">
          <div className="flex h-11 w-11 items-center justify-center rounded-xl bg-red-50 text-[var(--primary,#e3000b)] border border-red-200">
            <Store className="h-5 w-5" />
          </div>
          <div className="flex items-center gap-2">
            <Badge variant="outline" className="bg-red-50 text-[var(--primary,#e3000b)] border-red-200 text-xs font-bold">
              Säule 1
            </Badge>
            {isLive ? (
              <Badge variant="outline" className="bg-emerald-50 text-emerald-800 border-emerald-300 text-[11px] font-semibold">
                D1 Live
              </Badge>
            ) : (
              <Badge variant="outline" className="bg-slate-100 text-slate-700 border-slate-300 text-[11px] font-medium">
                Demo / Ungeprüft
              </Badge>
            )}
          </div>
        </div>

        {/* Title & Scope Description */}
        <div>
          <h3 className="font-display text-base font-bold text-slate-900 leading-snug">
            White-Label Partner-Shop
          </h3>
          <p className="mt-1 text-xs text-slate-600 leading-relaxed">
            {isLive ? (
              <>Vollwertiger B2B-Katalog mit <strong className="font-mono tabular-nums font-semibold text-slate-900">{displaySkuCount.toLocaleString('de-DE')} SKUs</strong> in Cloudflare D1 über 6 Haupt- und 38 Unterkategorien mit Multi-Tenant-Isolation.</>
            ) : (
              <>Exemplarischer B2B-Katalog (<strong className="font-mono tabular-nums font-semibold text-slate-900">{displaySkuCount.toLocaleString('de-DE')} SKUs (Demo)</strong>) in Cloudflare D1 Schema-Vorschau über Multi-Tenant-Partitionen.</>
            )}
          </p>
        </div>

        {/* Live Metrics Container */}
        <div className="space-y-2.5 rounded-xl bg-slate-50/80 p-3.5 text-xs border border-slate-100">
          <div className="flex items-center justify-between" data-testid="shop-sku-counter">
            <span className="flex items-center gap-1.5 text-slate-600">
              <Package className="h-3.5 w-3.5 text-slate-400" />
              <span>Artikelbestand:</span>
            </span>
            <span className="font-mono tabular-nums font-bold text-slate-900">
              {isLive ? `${displaySkuCount.toLocaleString('de-DE')} SKUs` : `${displaySkuCount.toLocaleString('de-DE')} SKUs (Demo)`}
            </span>
          </div>

          <div className="flex items-center justify-between" data-testid="shop-tenants-list">
            <span className="flex items-center gap-1.5 text-slate-600">
              <Users className="h-3.5 w-3.5 text-slate-400" />
              <span>Aktive Mandanten:</span>
            </span>
            <span className="font-semibold text-slate-900">
              {Array.isArray(displayTenants)
                ? (displayTenants.length > 0
                    ? `${displayTenants.length} Mandanten (${displayTenants.slice(0, 2).join(', ')}...)`
                    : '0 Mandanten')
                : `${displayTenants} Mandanten`}
            </span>
          </div>

          <div className="flex items-center justify-between" data-testid="shop-catalog-timestamp">
            <span className="flex items-center gap-1.5 text-slate-600">
              <Clock className="h-3.5 w-3.5 text-slate-400" />
              <span>Preiskatalog-Stand:</span>
            </span>
            <span className="font-mono tabular-nums font-semibold text-slate-900">
              {displayTimestamp}
            </span>
          </div>

          <div className="flex items-center justify-between pt-1 border-t border-slate-200">
            <span className="flex items-center gap-1.5 text-slate-600">
              <Layers className="h-3.5 w-3.5 text-slate-400" />
              <span>Katalog-Status:</span>
            </span>
            {isLive ? (
              <span className="inline-flex items-center gap-1 font-semibold text-emerald-700">
                <CheckCircle2 className="h-3 w-3" />
                <span>100% synchron</span>
              </span>
            ) : (
              <span className="inline-flex items-center gap-1 font-medium text-slate-600">
                <span>Demo-Vorschau (Nicht synchronisiert)</span>
              </span>
            )}
          </div>
        </div>

        {/* Detailed Mode: Categories breakdown */}
        {isDetailed && (
          <div className="space-y-2 pt-2" data-testid="shop-categories-breakdown">
            <h4 className="font-display text-xs font-bold text-slate-800 uppercase tracking-wider">
              Kategorie-Struktur (Muster: 6 Hauptbereiche, 38 Unterkategorien)
            </h4>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 text-xs">
              {SHOP_CANONICAL_CATEGORIES.map((cat, idx) => (
                <div key={idx} className="flex items-center justify-between rounded-lg bg-white border border-slate-200 p-2">
                  <span className="font-medium text-slate-800 truncate">{cat.name}</span>
                  <span className="font-mono tabular-nums text-slate-600 text-[11px] shrink-0 ml-1">
                    {cat.skus} SKUs (Muster)
                  </span>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>

      {/* Action Buttons Footer */}
      <div className="mt-6 pt-4 border-t border-slate-100 flex flex-wrap items-center justify-between gap-2">
        <div className="flex items-center gap-1.5">
          <Button
            type="button"
            variant="outline"
            size="sm"
            data-testid="shop-action-details"
            onClick={handleOpenPillar}
            className="text-xs font-bold gap-1 text-[var(--primary,#e3000b)] hover:bg-[var(--primary,#e3000b)]/5 cursor-pointer focus-visible:ring-2 focus-visible:ring-[var(--primary)]"
          >
            <span>Katalogverwaltung</span>
            <ChevronRight className="h-3.5 w-3.5" />
          </Button>

          <Button
            type="button"
            variant="ghost"
            size="sm"
            data-testid="shop-action-mandanten"
            onClick={handleOpenPillar}
            className="text-xs text-slate-600 hover:text-slate-900 gap-1 cursor-pointer hidden sm:flex"
          >
            <Building2 className="h-3 w-3" />
            <span>Mandanten</span>
          </Button>
        </div>

        <div className="flex items-center gap-1.5">
          <Button
            type="button"
            variant="ghost"
            size="sm"
            data-testid="shop-action-storefront"
            onClick={handleStorefrontClick}
            className="text-xs text-slate-600 hover:text-slate-900 gap-1 cursor-pointer"
          >
            <span>Zum Shop</span>
            <ExternalLink className="h-3 w-3" />
          </Button>

          <Button
            type="button"
            variant="default"
            size="sm"
            data-testid="shop-action-admin"
            onClick={handleAdminClick}
            className="text-xs font-bold gap-1 bg-slate-900 text-white hover:bg-slate-800 cursor-pointer focus-visible:ring-2 focus-visible:ring-[var(--primary)]"
          >
            <Layers className="h-3 w-3" />
            <span className="hidden sm:inline">Admin</span>
            <ArrowUpRight className="h-3 w-3" />
          </Button>
        </div>
      </div>
    </div>
  );
}
